import type { MetaAdsCapabilities } from "@/shared/types/accounts";
import { normalizeAdAccountId } from "@insights/module/boosted/boosted-params";

export type MetaAdsSettingsFacebookAccount = {
  sourceAccountId: string;
  login: string;
  name: string;
  platformType: string;
  platformId: string | null;
  pageId: string | null;
  pageName: string | null;
  metaAdsCapabilities?: MetaAdsCapabilities;
};

export type MetaAdsSettingsAccount = {
  boostAdAccountId: string;
  metaAdAccountId: string | null;
  name: string;
  currency?: string;
  timezoneName?: string;
  isEnabled?: boolean;
  status?: "available" | "unavailable" | "unknown";
  sourceAccountId?: string | null;
  facebookAccounts?: MetaAdsSettingsFacebookAccount[];
  metaAdsCapabilities?: MetaAdsCapabilities;
};

export type MetaAdsSettingsResponse = {
  canEdit: boolean;
  settings: {
    enabledAdAccounts: MetaAdsSettingsAccount[];
    availableAdAccounts: MetaAdsSettingsAccount[];
  };
  discovery?: {
    status: "idle" | "partial" | "complete" | "paused" | "unavailable";
    reason?:
      | "rate_limit"
      | "timeout"
      | "upstream"
      | "reconnect_required"
      | "page_limit"
      | "size_limit"
      | "connection_limit"
      | "cache"
      | "busy";
    totalConnections: number;
    completedConnections: number;
    failedConnections: number;
    discoveredAccounts: number;
    canResume: boolean;
    canAutoResume?: boolean;
    retryAfterSeconds: number;
    connectionLimit?: number;
    updatedAt?: string;
    reconnectAccounts?: Array<{ sourceAccountId: string; name: string }>;
    additionalReconnectAccounts?: number;
  };
};

export const metaAdsSettingsQueryKey = [
  "profile-settings",
  "meta-ads-settings",
] as const;

export function getEnabledMetaAdsAccountIds(
  enabledAdAccounts: MetaAdsSettingsAccount[] = []
) {
  return enabledAdAccounts.map((account) => account.boostAdAccountId);
}

export function getEnabledAvailableMetaAdsAccounts(
  settings?: MetaAdsSettingsResponse["settings"]
) {
  if (!settings) {
    return [];
  }

  const enabledIds = new Set(
    getEnabledMetaAdsAccountIds(settings.enabledAdAccounts).map((id) =>
      normalizeAdAccountId(id)
    )
  );

  const available = new Map(
    settings.availableAdAccounts.map((entry) => {
      const account = canonicalMetaAdsAccount(entry);
      return [account.boostAdAccountId, account];
    })
  );
  // "Not checked yet" is not "unavailable". Saved choices remain usable while
  // Meta is slow; campaign creation still performs its own permission checks.
  for (const entry of settings.enabledAdAccounts) {
    const account = canonicalMetaAdsAccount(entry);
    if (
      account.status === "unknown" &&
      !available.has(account.boostAdAccountId)
    )
      available.set(account.boostAdAccountId, account);
  }
  return [...available.values()].filter(
    (account) =>
      enabledIds.has(account.boostAdAccountId) &&
      // Reporting-only ad accounts remain visible to Insights, but cannot be
      // assigned to a group for campaign creation.
      account.metaAdsCapabilities?.boosting !== false
  );
}

function canonicalMetaAdsAccount(account: MetaAdsSettingsAccount) {
  return {
    ...account,
    boostAdAccountId:
      normalizeAdAccountId(account.boostAdAccountId) ||
      account.boostAdAccountId,
  };
}

export function getNonBoostingEnabledMetaAdsAccounts(
  settings?: MetaAdsSettingsResponse["settings"]
) {
  if (!settings) {
    return [];
  }

  return settings.enabledAdAccounts.filter(
    (account) => account.metaAdsCapabilities?.boosting === false
  );
}

export function getUnavailableEnabledMetaAdsAccounts(
  settings?: MetaAdsSettingsResponse["settings"]
) {
  if (!settings) {
    return [];
  }

  const availableIds = new Set(
    settings.availableAdAccounts.map((account) => account.boostAdAccountId)
  );

  return settings.enabledAdAccounts.filter(
    (account) =>
      account.status !== "unknown" &&
      (account.status === "unavailable" ||
        !availableIds.has(account.boostAdAccountId))
  );
}

export function getMetaAdsAccountByBoostId(
  settings: MetaAdsSettingsResponse["settings"] | undefined,
  boostAdAccountId: string | null | undefined
) {
  const canonicalId = normalizeAdAccountId(boostAdAccountId);
  if (!settings || !canonicalId) {
    return null;
  }

  const enabledAccount = settings.enabledAdAccounts.find(
    (account) => normalizeAdAccountId(account.boostAdAccountId) === canonicalId
  );
  const availableAccount = settings.availableAdAccounts.find(
    (account) => normalizeAdAccountId(account.boostAdAccountId) === canonicalId
  );

  if (availableAccount) {
    // Live discovery is the freshest source for ad-account metadata, while
    // the saved entry is the only place that retains the chosen source
    // connection. Carry that selection across so the group form can warn when
    // the saved source has become reporting-only.
    return canonicalMetaAdsAccount(
      enabledAccount?.sourceAccountId && !availableAccount.sourceAccountId
        ? {
            ...availableAccount,
            sourceAccountId: enabledAccount.sourceAccountId,
          }
        : availableAccount
    );
  }

  return enabledAccount ? canonicalMetaAdsAccount(enabledAccount) : null;
}

export function formatAssociatedFacebookAccounts(
  account: Pick<MetaAdsSettingsAccount, "facebookAccounts">
) {
  const names = (account.facebookAccounts || [])
    .map((facebookAccount) => facebookAccount.name || facebookAccount.login)
    .filter(Boolean);

  return names.join(", ");
}
